import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DollarSign, ArrowRight, Gauge } from 'lucide-react';
import { PlatformTopBarWrapper } from './PlatformTopBarWrapper';
import { dashboardApi } from '../services/dashboardApi';
import { useAuth } from '../auth/ParsleeAuthProvider';
import type { CurrentUsageSummaryResponse } from '../models/types';
import '../styles/App.css';

const DEFAULT_ORGANIZATION_ID = 'org_button_up';

function BillingPage() {
  // Auth is guaranteed by ProtectedRoute + StaffGate (U2); the top bar owns
  // identity/org/logout via PlatformTopBarWrapper (U4). This component only needs
  // the active org id to scope its data fetch.
  const { user } = useAuth();
  const [usageSummary, setUsageSummary] = useState<CurrentUsageSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const organizationId = user?.organizationId || DEFAULT_ORGANIZATION_ID;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await dashboardApi.getCurrentUsageSummary(organizationId);
        setUsageSummary(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load billing data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organizationId]);

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const portal = await dashboardApi.getBillingPortalUrl(organizationId);
      window.open(portal.url, '_blank');
    } catch (err) {
      setError('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatNumber = (num: number): string => num.toLocaleString('en-US');
  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  const formatDate = (dateStr: string): string =>
    new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const renderContent = (content: React.ReactNode) => (
    <>
      <PlatformTopBarWrapper />
      <div style={{ paddingTop: 'var(--topbar-height)', minHeight: '100vh' }}>
        {content}
      </div>
    </>
  );

  if (loading) {
    return renderContent(
      <div className="loading-container">
        <Spinner size={32} label="Loading billing information..." />
      </div>
    );
  }

  if (error) {
    return renderContent(
      <div className="error-container">
        <h1 className="text-2xl font-bold text-foreground">Error</h1>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  if (!usageSummary) {
    return renderContent(
      <div className="error-container">
        <p className="text-sm">No billing data available. Create a subscription to get started.</p>
      </div>
    );
  }

  const { tokenUsage } = usageSummary;
  const usagePercent = tokenUsage.included > 0 ? Math.min((tokenUsage.used / tokenUsage.included) * 100, 100) : 0;

  return renderContent(
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1 className="text-2xl font-bold text-foreground">Billing</h1>
        <Button onClick={handleManageBilling} disabled={portalLoading}>
          {portalLoading ? 'Opening...' : 'Manage Billing'}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        {/* Billing Period */}
        <Card>
          <div className="p-4">
            <p className="text-sm font-semibold text-foreground">Billing Period</p>
            <span className="text-xs text-muted-foreground">
              {formatDate(usageSummary.billingPeriodStart)} — {formatDate(usageSummary.billingPeriodEnd)}
            </span>
          </div>
        </Card>

        {/* Estimated Cost */}
        <Card>
          <div className="flex items-center gap-3 p-4 pb-0">
            <DollarSign className="h-6 w-6 text-accent-text" />
            <div>
              <p className="text-sm font-semibold text-foreground">Estimated Overage</p>
              <span className="text-xs text-muted-foreground">
                {tokenUsage.overage > 0
                  ? `${formatNumber(tokenUsage.overage)} tokens over limit`
                  : 'Within included tokens'}
              </span>
            </div>
          </div>
          <div style={{ padding: '0 16px 16px' }}>
            <h2 className="text-xl font-semibold text-foreground">{formatCurrency(tokenUsage.overageCostEstimate)}</h2>
          </div>
        </Card>
      </div>

      {/* Token Usage */}
      <Card style={{ marginBottom: '24px' }}>
        <div className="flex items-center gap-3 p-4 pb-0">
          <Gauge className="h-6 w-6 text-accent-text" />
          <div>
            <p className="text-sm font-semibold text-foreground">Token Usage</p>
            <span className="text-xs text-muted-foreground">
              {formatNumber(tokenUsage.used)} of {formatNumber(tokenUsage.included)} tokens used
            </span>
          </div>
        </div>
        <div style={{ padding: '0 16px 16px' }}>
          <div className="h-2 w-full rounded-full bg-secondary" style={{ margin: '16px 0' }}>
            <div className="h-full rounded-full bg-primary" style={{ width: `${usagePercent}%` }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <span className="block text-xs text-muted-foreground">Included</span>
              <p className="text-sm">{formatNumber(tokenUsage.included)}</p>
            </div>
            <div>
              <span className="block text-xs text-muted-foreground">Used</span>
              <p className="text-sm">{formatNumber(tokenUsage.used)}</p>
            </div>
            <div>
              <span className="block text-xs text-muted-foreground">Remaining</span>
              <p className="text-sm">{formatNumber(tokenUsage.remaining)}</p>
            </div>
            <div>
              <span className="block text-xs text-muted-foreground">Overage</span>
              <p className="text-sm">{tokenUsage.overage > 0 ? formatNumber(tokenUsage.overage) : '-'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Meter Breakdowns */}
      {usageSummary.meterBreakdowns.length > 0 && (
        <Card>
          <div className="p-4 pb-0">
            <p className="text-sm font-semibold text-foreground">Usage by Meter</p>
          </div>
          <div className="p-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Meter</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Unit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usageSummary.meterBreakdowns.map((meter) => (
                  <TableRow key={meter.meterType}>
                    <TableCell>{meter.meterType}</TableCell>
                    <TableCell>{formatNumber(meter.quantity)}</TableCell>
                    <TableCell>{meter.unit}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default BillingPage;
